import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Fingerprint,
  Loader2,
  Plus,
  Smartphone,
  Trash2,
  Cloud,
  ShieldCheck,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  deletePasskey,
  isPasskeySupported,
  listPasskeys,
  registerPasskey,
  type Passkey,
} from '@/api/passkeys'

const PASSKEYS_QUERY_KEY = ['passkeys'] as const

/**
 * Gestion des passkeys (WebAuthn) de l'utilisateur courant.
 *
 * Pattern aligné sur les autres sections du profil (CompanyNameSection,
 * SecuritySection). L'enrôlement déclenche le prompt biométrique système ;
 * le bouton n'apparaît que si le navigateur supporte WebAuthn.
 */
export function PasskeysSection({ darkMode }: { darkMode: boolean }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const supported = isPasskeySupported()

  const [name, setName] = useState('')
  const [adding, setAdding] = useState(false)

  const {
    data: passkeys = [],
    isLoading,
  } = useQuery({
    queryKey: PASSKEYS_QUERY_KEY,
    queryFn: listPasskeys,
    enabled: supported,
    staleTime: 30_000,
  })

  const registerMutation = useMutation({
    mutationFn: (label: string) => registerPasskey(label),
    onSuccess: () => {
      toast.success(t('profile.passkeys.added', "Clé d'accès enregistrée"))
      setName('')
      setAdding(false)
      void queryClient.invalidateQueries({ queryKey: PASSKEYS_QUERY_KEY })
    },
    onError: (error) => {
      if (
        error instanceof DOMException &&
        (error.name === 'NotAllowedError' || error.name === 'AbortError')
      ) {
        // L'utilisateur a fermé le prompt — pas une erreur à afficher
        return
      }
      console.error('[Passkeys] register failed', error)
      toast.error(t('profile.passkeys.addError', "Impossible d'enregistrer la clé d'accès"))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePasskey(id),
    onSuccess: () => {
      toast.success(t('profile.passkeys.deleted', "Clé d'accès supprimée"))
      void queryClient.invalidateQueries({ queryKey: PASSKEYS_QUERY_KEY })
    },
    onError: () => {
      toast.error(t('profile.passkeys.deleteError', 'Suppression impossible'))
    },
  })

  if (!supported) {
    // Pas de bruit : on masque la section entière si l'environnement ne
    // supporte pas WebAuthn (Safari très ancien, contexte non-HTTPS, etc.)
    return null
  }

  const handleAdd = () => {
    const trimmed = name.trim() || defaultPasskeyName()
    registerMutation.mutate(trimmed)
  }

  return (
    <section
      className={cn(
        'rounded-xl border p-5',
        darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
      )}
    >
      <div className="flex items-center justify-between mb-1">
        <h2
          className={cn(
            'text-sm font-semibold flex items-center gap-2',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          <Fingerprint className="h-4 w-4 text-indigo-500" />
          {t('profile.passkeys.title', "Clés d'accès")}
        </h2>
        {!adding && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAdding(true)}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {t('profile.passkeys.add', "Ajouter une clé d'accès")}
          </Button>
        )}
      </div>
      <p
        className={cn(
          'text-xs mb-4',
          darkMode ? 'text-slate-400' : 'text-slate-500',
        )}
      >
        {t(
          'profile.passkeys.hint',
          "Connexion rapide et sans mot de passe, par empreinte ou Face ID.",
        )}
      </p>

      {adding && (
        <div className="mb-4 space-y-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
          <div>
            <Label className="text-xs font-medium">
              {t('profile.passkeys.nameLabel', "Nom de la clé d'accès")}
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(
                'profile.passkeys.namePlaceholder',
                'Ex : MacBook Pro perso',
              )}
              maxLength={100}
              autoFocus
              disabled={registerMutation.isPending}
            />
            <p
              className={cn(
                'mt-1 text-xs',
                darkMode ? 'text-slate-500' : 'text-slate-500',
              )}
            >
              {t(
                'profile.passkeys.namePromptHint',
                "Votre appareil va demander une authentification biométrique.",
              )}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setAdding(false)
                setName('')
              }}
              disabled={registerMutation.isPending}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAdd}
              disabled={registerMutation.isPending}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {registerMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('profile.passkeys.confirmAdd', "Enregistrer la clé d'accès")}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-4 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : passkeys.length === 0 ? (
        <p
          className={cn(
            'text-sm py-2',
            darkMode ? 'text-slate-500' : 'text-slate-500',
          )}
        >
          {t(
            'profile.passkeys.empty',
            "Aucune clé d'accès enregistrée. Ajoutez-en une pour vous connecter sans mot de passe.",
          )}
        </p>
      ) : (
        <ul className="divide-y divide-slate-200 dark:divide-slate-800">
          {passkeys.map((passkey) => (
            <PasskeyRow
              key={passkey.id}
              passkey={passkey}
              darkMode={darkMode}
              onDelete={() => deleteMutation.mutate(passkey.id)}
              isDeleting={deleteMutation.isPending && deleteMutation.variables === passkey.id}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function PasskeyRow({
  passkey,
  darkMode,
  onDelete,
  isDeleting,
}: {
  passkey: Passkey
  darkMode: boolean
  onDelete: () => void
  isDeleting: boolean
}) {
  const { t, i18n } = useTranslation()
  const created = formatDate(passkey.created_at, i18n.language)
  const lastUsed = passkey.last_used_at
    ? formatDate(passkey.last_used_at, i18n.language)
    : null

  return (
    <li className="py-3 flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 min-w-0">
        <div
          className={cn(
            'mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0',
            darkMode ? 'bg-slate-800' : 'bg-slate-100',
          )}
        >
          {passkey.backed_up ? (
            <Cloud className="h-4 w-4 text-indigo-500" />
          ) : (
            <Smartphone className="h-4 w-4 text-slate-500" />
          )}
        </div>
        <div className="min-w-0">
          <p
            className={cn(
              'text-sm font-medium truncate',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {passkey.name}
          </p>
          <p
            className={cn(
              'text-xs flex items-center gap-1.5 flex-wrap',
              darkMode ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            <ShieldCheck className="h-3 w-3" />
            {passkey.backed_up
              ? t('profile.passkeys.synced', 'Synchronisée entre appareils')
              : t('profile.passkeys.deviceBound', 'Liée à cet appareil')}
            <span aria-hidden>·</span>
            <span>
              {t('profile.passkeys.createdOn', 'Ajoutée le {{date}}', { date: created })}
            </span>
            {lastUsed && (
              <>
                <span aria-hidden>·</span>
                <span>
                  {t('profile.passkeys.lastUsedOn', 'Dernière utilisation : {{date}}', {
                    date: lastUsed,
                  })}
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('profile.passkeys.confirmDeleteTitle', "Supprimer cette clé d'accès ?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'profile.passkeys.confirmDeleteDesc',
                "Vous ne pourrez plus vous connecter depuis cet appareil avec cette clé d'accès. Vous pourrez en créer une nouvelle à tout moment.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('common.cancel', 'Annuler')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('common.delete', 'Supprimer')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  )
}

function defaultPasskeyName(): string {
  const ua = navigator.userAgent
  if (/Mac/i.test(ua)) return 'Mac'
  if (/iPhone/i.test(ua)) return 'iPhone'
  if (/iPad/i.test(ua)) return 'iPad'
  if (/Android/i.test(ua)) return 'Android'
  if (/Windows/i.test(ua)) return 'Windows'
  return 'Passkey'
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}
